import React, { useCallback, useEffect, useRef, useState } from "react";
import EmptyState from "./EmptyState";

/**
 * Tracks whether a scroll container has content hidden to the left or right.
 *
 * A table that scrolls with no visible edge just looks cropped — a row ends
 * mid-chip and the columns beyond it (status, expiry, the action buttons) are
 * easy to miss entirely. The fades this drives are the only cue that they are
 * there.
 */
const useHorizontalOverflow = (ref) => {
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;
    setEdges({
      left: el.scrollLeft > 1,
      // 1px of slack: sub-pixel layout leaves a fraction behind at full scroll.
      right: max > 1 && el.scrollLeft < max - 1,
    });
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    measure();
    el.addEventListener("scroll", measure, { passive: true });

    // Column count and content width both change as data loads.
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [ref, measure]);

  return [edges, measure];
};

/**
 * The table shell every list page shares.
 *
 * Each page used to carry its own copy of the card border, header row, hover
 * treatment, skeleton and empty state, and they had drifted: some scrolled
 * horizontally and some clipped, the hover colour was a hard-coded
 * `bg-gray-100/5` that is invisible in light mode, and the skeletons guessed a
 * different column count than the table they stood in for. Fixing any of that
 * meant fixing it once per page, so it only ever got fixed on some of them.
 *
 * Columns use the shape the license product handlers already emit, so those
 * pass their `tableColumns()` array straight through:
 *
 *   { key, label, render?(row), nowrap?, align?, width? }
 *
 * `render` is optional — without it the cell falls back to `row[key]`.
 */
const DataTable = ({
  columns,
  rows,
  loading = false,
  skeletonRows = 5,
  getRowKey = (row, index) => row._id || row.id || index,
  emptyState,
  onRowClick,
}) => {
  const scrollRef = useRef(null);
  const [edges, remeasure] = useHorizontalOverflow(scrollRef);

  // Row count and loading state both change the rendered width.
  useEffect(remeasure, [rows, loading, columns.length, remeasure]);

  const alignClass = (align) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  return (
    <div
      className="relative rounded-lg border overflow-hidden"
      style={{ background: "var(--card-bg)", borderColor: "var(--card-border)" }}
    >
      {/* Edge fades marking content scrolled out of view. Pointer-events-none
          so they never swallow a click on the row beneath. */}
      {edges.left && (
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to right, var(--card-bg), transparent)" }}
        />
      )}
      {edges.right && (
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 bottom-0 w-10 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to left, var(--card-bg), transparent)" }}
        />
      )}

      {/* The only scroll container. Wide tables scroll here rather than
          stretching the page, which is what keeps narrow screens usable. */}
      <div ref={scrollRef} className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: "max-content" }}>
          <thead style={{ background: "var(--table-header-bg)" }}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${alignClass(
                    col.align
                  )}`}
                  style={{ color: "var(--main-text-muted)", width: col.width }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{ borderTop: "1px solid var(--table-row-border)" }}
                >
                  {/* One skeleton cell per real column, so the placeholder has
                      the same shape as the table it is standing in for. */}
                  {columns.map((col, colIndex) => (
                    <td key={col.key} className="px-4 py-4">
                      <div
                        className="h-4 rounded animate-pulse"
                        style={{
                          background: "var(--card-border)",
                          width: `${55 + ((colIndex + rowIndex) % 3) * 15}%`,
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  {emptyState || <EmptyState />}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={getRowKey(row, index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`table-row-hover transition-colors ${
                    onRowClick ? "cursor-pointer" : ""
                  }`}
                  style={{ borderTop: "1px solid var(--table-row-border)" }}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-4 align-middle ${alignClass(col.align)} ${
                        col.nowrap ? "whitespace-nowrap" : ""
                      }`}
                      style={{ color: "var(--main-text)" }}
                    >
                      {col.render ? col.render(row) : row[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
